import { useParams, useOutletContext } from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import { useEffect } from 'react';
import { TransactionWarnings, formatBalance, formatDate } from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  setBlacklistData,
  setBlacklistDataVisible,
  setBlacklistDataItem,
  setBlacklistItemVisible,
  changePanelState,
} from '../store/slices/internal-transaction';
import {
  useCreateProblemTransactionChatMutation,
  useLazyGetProblemTransactionRecipientClientsOfficiasQuery,
  useLazyGetProblemTransactionSenderClientsOfficiasQuery,
  useLazyGetCustomerInternalTransactionWarningQuery,
} from '../store/services/customers';

const InternalTransactionWarning = (): React.JSX.Element => {
  const {
    customersProblemTransactions: {
      internal: {
        warnings,
        transaction,
        senderCompanyOfficials,
        recipientCompanyOfficials,
        chats,
      },
    },
    customersDictionaries: {
      customersTransactionTypes,
      currentEmployee,
      currenciesList,
    },
  } = useAppSelector((state) => state);

  const { sendMessage } = useOutletContext<{ sendMessage: SendMessage }>();

  const [createTransactionChat, { isLoading: chatCreateInProgress }] =
    useCreateProblemTransactionChatMutation();

  const dispatch = useAppDispatch();

  const { transactionId } = useParams();

  const [getSenderAccounts] =
    useLazyGetProblemTransactionSenderClientsOfficiasQuery();
  const [getRecipientAccounts] =
    useLazyGetProblemTransactionRecipientClientsOfficiasQuery();

  const [
    getInternalTransactionWarningById,
    { isLoading: isWarningByIdLoading, isFetching: isWarningByIdFetching },
  ] = useLazyGetCustomerInternalTransactionWarningQuery();

  const getInternalTransactionWarningByIdAction = async ({
    id,
    warningId,
  }: {
    id: string;
    warningId: string;
  }) => {
    await getInternalTransactionWarningById({
      id,
      warningId,
    });
  };

  const createChatWithMessage = async ({
    message,
    userId,
    type,
  }: {
    message: string;
    userId?: string;
    type?: 'sender' | 'recipient';
  }) => {
    const title = `RFI: Internal transaction ${formatBalance(
      transaction?.sum || 0,
      currenciesList?.find((item) => item.id === transaction?.currencyId),
    )} ${transaction?.currency?.name} ${
      transaction?.transferDate ? formatDate(transaction?.transferDate) : ''
    }`;

    const body: {
      title: string;
      employeeId: string;
      organizationId: string;
      userId?: string;
    } = {
      title,
      employeeId: currentEmployee?.id as string,
      organizationId:
        type === 'recipient'
          ? transaction?.recipientAccount?.organizationId
          : transaction?.senderAccount?.organizationId,
    };

    if (userId) {
      body.userId = userId;
    }

    const response = (await createTransactionChat({
      id: transactionId as string,
      body,
    })) as { data: any };

    if (response && response.data && sendMessage) {
      sendMessage(
        JSON.stringify({
          event: 'subscribe',
          data: { channelId: response?.data?.data?.id },
        }),
      );

      sendMessage(
        JSON.stringify({
          event: 'message',
          data: {
            channelId: response?.data?.data?.id,
            content: message,
          },
        }),
      );

      sendMessage(
        JSON.stringify({
          event: 'unsubscribe',
          data: { channelId: response?.data?.data?.id },
        }),
      );
    }
  };

  useEffect(() => {
    if (
      transaction &&
      transaction.senderAccount &&
      transaction.senderAccount?.organization?.type === 'CORPORATE' &&
      !senderCompanyOfficials
    ) {
      getSenderAccounts(transaction.senderAccount.organizationId);
    }
  }, [transaction, senderCompanyOfficials]);

  useEffect(() => {
    if (
      transaction &&
      transaction.recipientAccount &&
      transaction.recipientAccount?.organization?.type === 'CORPORATE' &&
      !recipientCompanyOfficials
    ) {
      getRecipientAccounts(transaction.recipientAccount.organizationId);
    }
  }, [transaction]);

  return (
    <TransactionWarnings
      warnings={warnings}
      employee={undefined}
      transactionsTypes={customersTransactionTypes}
      setBlacklistDataVisible={(visible) => {
        dispatch(setBlacklistDataVisible(visible));
        dispatch(changePanelState({ state: false }));
      }}
      setBlacklistData={(data) => dispatch(setBlacklistData(data))}
      setBlacklistItemVisible={(visible) => {
        dispatch(setBlacklistItemVisible(visible));
        dispatch(changePanelState({ state: false }));
      }}
      setBlacklistDataItem={(data) => dispatch(setBlacklistDataItem(data))}
      transactionUrlType="internal"
      transactionId={transactionId}
      recipients={recipientCompanyOfficials}
      senders={senderCompanyOfficials}
      recipientAccount={transaction?.recipientAccount}
      senderAccount={transaction?.senderAccount}
      chats={chats}
      createChatWithMessage={createChatWithMessage}
      chatCreateInProgress={chatCreateInProgress}
      getWarningById={getInternalTransactionWarningByIdAction}
      isWarningByIdLoading={isWarningByIdLoading}
      isWarningByIdFetching={isWarningByIdFetching}
      isInternal
      withTransactionUrl
    />
  );
};

export default InternalTransactionWarning;
